import React, { useState, useEffect } from 'react';
import { Link, useLocation } from 'react-router-dom';
import { ArrowUpRight, Menu, X } from 'lucide-react';
import AuditModal from './AuditModal';

const navItems = [
  { name: 'Home', path: '/' },
  { name: 'Services', path: '/services' },
  { name: 'Case Studies', path: '/case-studies' },
  { name: 'Newsletter', path: '/newsletter' },
  { name: 'About', path: '/about' },
  { name: 'Blog', path: '/blog' },
  { name: 'Contact', path: '/contact' },
];

const Header = () => {
  const location = useLocation();
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [isMobileOpen, setIsMobileOpen] = useState(false);
  const [scrolled, setScrolled] = useState(false);

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 12);
    onScroll();
    window.addEventListener('scroll', onScroll, { passive: true });
    return () => window.removeEventListener('scroll', onScroll);
  }, []);

  useEffect(() => { setIsMobileOpen(false); }, [location.pathname]);

  return (
    <>
      <header
        data-testid="site-header"
        className={`fixed top-0 left-0 right-0 z-50 transition-all duration-300 ${
          scrolled
            ? 'bg-[rgba(253,251,247,0.85)] backdrop-blur-xl border-b border-line'
            : 'bg-transparent border-b border-transparent'
        }`}
      >
        <div className="container-x py-4 flex items-center justify-between">
          <Link to="/" data-testid="logo-link" className="flex items-center">
            <img
              src="/logo.png"
              alt="Growth Hackeng"
              className="h-14 md:h-16 w-auto object-contain -my-2"
            />
          </Link>

          <nav className="hidden lg:flex items-center gap-1">
            {navItems.map((item) => {
              const active = location.pathname === item.path;
              return (
                <Link
                  key={item.name}
                  to={item.path}
                  data-testid={`nav-${item.name.toLowerCase().replace(/\s+/g, '-')}`}
                  className={`relative px-4 py-2 text-sm font-medium transition-colors rounded-full ${
                    active ? 'text-ink' : 'text-muted-warm hover:text-ink'
                  }`}
                >
                  {item.name}
                  {active && (
                    <span className="absolute left-1/2 -bottom-0.5 -translate-x-1/2 w-1 h-1 rounded-full bg-[var(--gh-orange)]" />
                  )}
                </Link>
              );
            })}
          </nav>

          <div className="flex items-center gap-3">
            <button
              onClick={() => setIsModalOpen(true)}
              data-testid="header-book-audit-btn"
              className="hidden sm:inline-flex btn-orange text-sm py-2.5 px-5"
            >
              Book Free Audit
              <ArrowUpRight className="w-4 h-4" />
            </button>
            <button
              onClick={() => setIsMobileOpen((v) => !v)}
              data-testid="mobile-menu-toggle"
              className="lg:hidden inline-flex items-center justify-center w-10 h-10 rounded-full border border-line bg-cream"
              aria-label="Toggle menu"
            >
              {isMobileOpen ? <X className="w-5 h-5" /> : <Menu className="w-5 h-5" />}
            </button>
          </div>
        </div>

        {isMobileOpen && (
          <div className="lg:hidden bg-cream border-t border-line" data-testid="mobile-menu">
            <div className="container-x py-6 flex flex-col gap-2">
              {navItems.map((item) => (
                <Link
                  key={item.name}
                  to={item.path}
                  data-testid={`mobile-nav-${item.name.toLowerCase().replace(/\s+/g, '-')}`}
                  className={`px-4 py-3 rounded-2xl text-base font-medium ${
                    location.pathname === item.path
                      ? 'bg-cream-2 text-ink'
                      : 'text-muted-warm hover:bg-cream-2 hover:text-ink'
                  }`}
                >
                  {item.name}
                </Link>
              ))}
              <button
                onClick={() => { setIsModalOpen(true); setIsMobileOpen(false); }}
                data-testid="mobile-book-audit-btn"
                className="btn-orange justify-center w-full mt-2"
              >
                Book Free Audit
                <ArrowUpRight className="w-4 h-4" />
              </button>
            </div>
          </div>
        )}
      </header>

      <AuditModal isOpen={isModalOpen} onClose={() => setIsModalOpen(false)} />
    </>
  );
};

export default Header;
